import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiFileText, FiShield, FiUsers, FiServer, FiMail, FiCreditCard, FiAlertTriangle } = FiIcons;

const TermsOfService = () => {
  const lastUpdated = '04 September 2025';

  const sections = [
    {
      title: 'About us',
      icon: FiUsers,
      content: {
        intro: 'Company Details:',
        items: [
          'Legal Entity: AdaptSA Business Solutions (Pty) Ltd',
          'Trading Division: Primehost',
          'Registration: South African Private Company',
          'Physical Address: 22 Magwa Crescent, Waterfall, Midrand, Gauteng, 1662',
          'Contact: +27 10 007 0690 | support@primehost.co.za'
        ]
      }
    },
    {
      title: 'Services provided',
      icon: FiServer,
      content: {
        subsections: [
          {
            title: 'Web Hosting Services',
            items: [
              'Shared hosting environments',
              'Virtual Private Servers (VPS)',
              'Dedicated server solutions',
              'Website management and maintenance'
            ]
          },
          {
            title: 'Email Hosting Services',
            items: [
              'Professional email accounts',
              'Email forwarding and management',
              'Spam and virus protection',
              'Email storage and backup'
            ]
          },
          {
            title: 'Web Design Services',
            items: [
              'Custom website development',
              'Website maintenance and updates',
              'Content management systems',
              'E-commerce solutions'
            ]
          },
          {
            title: 'Domain Registration Services',
            items: [
              'Domain name registration and renewal',
              'DNS management',
              'Domain transfer services',
              'Domain privacy protection'
            ]
          }
        ]
      }
    },
    {
      title: 'Acceptable use policy',
      icon: FiAlertTriangle,
      content: {
        intro: 'You may NOT use our services for:',
        subsections: [
          {
            title: 'Illegal Activities',
            items: [
              'Any activity that violates South African law or international law',
              'Drug-related content or transactions',
              'Child abuse material or exploitation',
              'Human trafficking or illegal immigration services',
              'Money laundering or financial fraud',
              'Terrorism or violent extremism',
              'Intellectual property infringement',
              'Identity theft or impersonation'
            ]
          },
          {
            title: 'Email Abuse',
            items: [
              'Sending unsolicited commercial emails (spam)',
              'Exceeding 100 emails per hour per domain',
              'Email harvesting or list building without consent',
              'Phishing or fraudulent email schemes',
              'Bulk mailing to purchased or rented lists'
            ]
          },
          {
            title: 'Technical Abuse',
            items: [
              'Cryptocurrency mining on shared hosting',
              'Resource abuse affecting other customers',
              'DDoS attacks or network abuse',
              'Malware hosting or distribution',
              'Hacking tools or exploits',
              'Brute force attacks',
              'Unauthorised access attempts'
            ]
          }
        ]
      }
    },
    {
      title: 'Payment terms',
      icon: FiCreditCard,
      content: {
        subsections: [
          {
            title: 'Payment Methods',
            items: [
              'We accept various payment methods including credit cards, bank transfers, and other approved methods as displayed during checkout'
            ]
          },
          {
            title: 'Billing Cycles',
            items: [
              'Services are billed in advance',
              'Invoices are generated according to your chosen billing cycle',
              'Payment is due immediately upon invoice generation'
            ]
          },
          {
            title: 'Late Payments',
            items: [
              'Services may be suspended 48 hours after the due date',
              'Late fees may apply as permitted by South African law',
              'Services may be terminated 7 days after suspension for non-payment'
            ]
          },
          {
            title: 'Refunds',
            items: [
              'Refunds are considered on a case-by-case basis',
              'Domain registrations are generally non-refundable',
              'Setup fees are non-refundable',
              'Refund requests must be submitted within 30 days of initial payment'
            ]
          }
        ]
      }
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Terms of service
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              The terms and conditions governing your use of Primehost services
            </p>
            <p className="text-sm text-gray-500">
              Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-primary-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Acceptance of terms</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                These Terms of Service ("Terms") govern your use of services provided by Primehost, 
                a division of AdaptSA Business Solutions (Pty) Ltd ("we," "us," "our," or "Primehost"), 
                including web hosting, email hosting, web design, and domain registration services. 
                By using our services, you agree to be bound by these Terms.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Sections */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-sm p-8"
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-primary-100 p-3 rounded-xl">
                    <SafeIcon icon={section.icon} className="w-6 h-6 text-primary-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 capitalize">
                    {section.title}
                  </h2>
                </div>

                {section.content.intro && (
                  <p className="text-gray-700 mb-4 font-medium">
                    {section.content.intro}
                  </p>
                )}

                {section.content.items && (
                  <ul className="space-y-2 mb-6">
                    {section.content.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2.5 flex-shrink-0"></div>
                        <span className="text-gray-700 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.content.subsections && (
                  <div className="space-y-6">
                    {section.content.subsections.map((subsection, subIndex) => (
                      <div key={subIndex}>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                          {subsection.title}
                        </h3>
                        <ul className="space-y-2">
                          {subsection.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-primary-500 rounded-full mt-2.5 flex-shrink-0"></div>
                              <span className="text-gray-700 leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Email Service Terms */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gray-50 rounded-2xl p-8"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="bg-teal-100 p-3 rounded-xl">
                <SafeIcon icon={FiMail} className="w-6 h-6 text-teal-700" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Email service specific terms</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Email Usage Limits</h3>
                <ul className="space-y-2">
                  {[
                    'Maximum 100 emails per hour per domain across all email accounts',
                    'No unsolicited commercial email (spam)',
                    'No email harvesting or automated list building',
                    'Professional business use only'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-teal-700 rounded-full mt-2.5 flex-shrink-0"></div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Email Deliverability</h3>
                <p className="text-gray-700">
                  We do not guarantee email delivery due to factors beyond our control, including 
                  recipient server configurations and spam filtering.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Legal Compliance */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-sm p-8"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="bg-red-100 p-3 rounded-xl">
                <SafeIcon icon={FiShield} className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Legal compliance</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Law Enforcement Cooperation</h3>
                <p className="text-gray-700 mb-3">
                  We may be bound by legal enforcement to provide any information required by law, including:
                </p>
                <ul className="space-y-2">
                  {[
                    'Responding to valid court orders and subpoenas',
                    'Cooperating with law enforcement investigations',
                    'Complying with regulatory requirements',
                    'Providing information for national security purposes'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-red-600 rounded-full mt-2.5 flex-shrink-0"></div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Content Compliance</h3>
                <p className="text-gray-700">
                  You must ensure all content and activities comply with South African laws and regulations, 
                  international laws where applicable, industry standards and best practices, and third-party 
                  terms of service and licenses.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Limitation of Liability */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-yellow-50 rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Limitation of liability</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Service Provision</h3>
                <p className="text-gray-700">
                  Services are provided "AS-IS" without warranties of any kind. We do not guarantee 
                  continuous service availability, data integrity or security, compatibility with 
                  third-party software, or specific performance metrics.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Liability Limits</h3>
                <p className="text-gray-700">
                  Our total liability is limited to the amount paid for services in the 12 months 
                  preceding the claim. We are not liable for indirect, consequential, or punitive 
                  damages, lost profits or business opportunities, data loss or corruption, 
                  third-party actions or failures, or force majeure events.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-primary-50 rounded-2xl p-8 text-center"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact information</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms or to report violations:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Primehost</strong></p>
              <p>22 Magwa Crescent, Waterfall, Midrand, Gauteng, 1662</p>
              <p>Email: support@primehost.co.za</p>
              <p>Phone: +27 10 007 0690</p>
              <p>Operating Hours: 08:00 to 16:00 (South African Standard Time)</p>
            </div>
            <p className="text-gray-600 mt-6 text-sm">
              By using our services, you acknowledge that you have read, understood, 
              and agree to be bound by these Terms of Service and our Privacy Policy.
            </p>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default TermsOfService;